import { formatPrice } from '../utils/formatPrice';
import { useState, useEffect, useRef } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { Search, Plus, Edit, Trash2, X, Check, ChevronLeft, ChevronRight, Upload, Download, Info } from 'lucide-react';
import { adminApi, ProductInput, CategoryWithSubcategories } from '../api/admin';
import AdminLayout from '../components/AdminLayout';
import ImageUpload from '../components/ImageUpload';

const emptyProduct: ProductInput = {
  category_id: 0,
  subcategory_id: 0,
  brand: '',
  ref: '',
  name: '',
  name_en: '',
  description: '',
  description_en: '',
  price: 0,
  reseller_price: null,
  stock: 0,
  moq: 1,
  quote_threshold: 10,
  features: [],
  features_en: [],
  images: [],
  variants: { sizes: [], colors: [] },
  norm_ids: [],
  sector_ids: [],
  bestseller: false,
  badge: '',
  badge_en: '',
  is_active: true,
};

export default function AdminProducts() {
  usePageTitle("Administration — Gestion des produits — Lodiss");

  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState<CategoryWithSubcategories[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any | null>(null);
  const [form, setForm] = useState<ProductInput>(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState<any>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, [page, search, categoryFilter]);

  const loadCategories = () => {
    adminApi.getCategories()
      .then(setCategories)
      .catch(console.error);
  };

  const loadProducts = () => {
    setLoading(true);
    adminApi.getProducts({ 
      page, 
      limit: 20, 
      search: search || undefined,
      category: categoryFilter || undefined,
    })
      .then(res => {
        setProducts(res.items);
        setTotal(res.total);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingProduct) {
        await adminApi.updateProduct(editingProduct.id, form);
        setShowModal(false);
        setEditingProduct(null);
        setForm(emptyProduct);
        loadProducts();
      } else {
        // Create product first
        const result = await adminApi.createProduct(form);
        // Open edit mode so user can add images
        const newProduct = { ...form, id: result.id };
        setEditingProduct(newProduct);
        setForm({
          ...form,
          images: [], // Reset images for the edit mode
        });
        // Don't close modal, let user add images
        loadProducts();
      }
    } catch (err) {
      console.error('Failed to save product:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await adminApi.deleteProduct(id);
      setDeleteConfirm(null);
      loadProducts();
    } catch (err) {
      console.error('Failed to delete product:', err);
    }
  };

  const handleToggleActive = async (product: any) => {
    try {
      await adminApi.updateProduct(product.id, { ...product, is_active: !product.is_active });
      loadProducts();
    } catch (err) {
      console.error('Failed to toggle product:', err);
    }
  };

  const openEdit = (product: any) => {
    setEditingProduct(product);
    setForm({
      category_id: product.category_id,
      subcategory_id: product.subcategory_id,
      brand: product.brand,
      ref: product.ref,
      name: product.name,
      name_en: product.name_en,
      description: product.description,
      description_en: product.description_en,
      price: product.price,
      reseller_price: product.reseller_price ?? null,
      stock: product.stock,
      moq: product.moq,
      quote_threshold: product.quote_threshold,
      features: product.features || [],
      features_en: product.features_en || [],
      images: product.images || [],
      variants: product.variants || { sizes: [], colors: [] },
      norm_ids: product.norm_ids || [],
      sector_ids: product.sector_ids || [],
      bestseller: product.bestseller,
      badge: product.badge,
      badge_en: product.badge_en,
      is_active: product.is_active,
    });
    setShowModal(true);
  };

  const handleCsvImport = async (file: File) => {
    setCsvImporting(true);
    try {
      const result = await adminApi.importProductsCsv(file);
      setCsvResult(result);
      loadProducts();
    } catch (err: any) {
      alert(err.message || 'Import failed');
    } finally {
      setCsvImporting(false);
    }
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Produits</h1>
          <div className="flex items-center gap-3">
            {/* CSV Import Section */}
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept=".csv"
                ref={csvInputRef}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleCsvImport(file);
                  e.target.value = '';
                }}
              />
              <button
                onClick={() => csvInputRef.current?.click()}
                disabled={csvImporting}
                className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                {csvImporting ? (
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Upload size={18} />
                )}
                Importer CSV
              </button>
              <div className="relative">
                <button
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <Info size={16} />
                </button>
                {showTooltip && (
                  <div className="absolute top-full mt-2 left-0 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 max-w-[260px] z-50">
                    Importez plusieurs produits en une seule fois via un fichier CSV.
                    Téléchargez le modèle pour connaître le format attendu.
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => { adminApi.downloadCsvTemplate().catch(err => alert(err.message || 'Download failed')); }}
              className="flex items-center gap-1 text-xs text-lodiss-red hover:underline"
            >
              <Download size={14} />
              Télécharger le modèle CSV
            </button>
            <button
              onClick={() => { setEditingProduct(null); setForm(emptyProduct); setShowModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-lodiss-red text-white rounded-lg hover:bg-lodiss-dark"
            >
              <Plus size={18} />
              Ajouter un produit
            </button>
          </div>
        </div>

        {/* CSV Import Result */}
        {csvResult && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <p className="text-emerald-800 font-medium">
                {csvResult.created} produit(s) créé(s), {csvResult.updated} mis à jour.
              </p>
              <button onClick={() => setCsvResult(null)} className="p-1 hover:bg-emerald-100 rounded">
                <X size={18} className="text-emerald-600" />
              </button>
            </div>
            {csvResult.errors && csvResult.errors.length > 0 && (
              <div className="mt-2">
                <p className="text-red-600 text-xs font-medium">Erreurs:</p>
                {csvResult.errors.slice(0, 5).map((err: any, idx: number) => (
                  <p key={idx} className="text-red-600 text-xs">
                    Ligne {err.row} ({err.ref}): {err.error}
                  </p>
                ))}
                {csvResult.errors.length > 5 && (
                  <p className="text-red-600 text-xs">...et {csvResult.errors.length - 5} autres erreurs</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-lodiss-red"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-lodiss-red"
          >
            <option value="">Toutes les catégories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.slug}>{cat.name_fr}</option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Réf</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Nom</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Catégorie</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Prix</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Stock</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Best.</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Statut</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center">
                    <div className="w-6 h-6 border-2 border-lodiss-red border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    Aucun produit trouvé
                  </td>
                </tr>
              ) : (
                products.map(product => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-sm">{product.ref}</td>
                    <td className="px-4 py-3 text-sm max-w-[200px] truncate">{product.name}</td>
                    <td className="px-4 py-3 text-sm">{product.category?.slug || '-'}</td>
                    <td className="px-4 py-3 text-sm">{formatPrice(product.price || 0)}</td>
                    <td className="px-4 py-3 text-sm">{product.stock}</td>
                    <td className="px-4 py-3">
                      {product.bestseller ? (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Oui</span>
                      ) : (
                        <span className="text-xs text-gray-400">Non</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleActive(product)}
                        className={`text-xs px-2 py-0.5 rounded ${product.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}
                      >
                        {product.is_active ? 'Actif' : 'Inactif'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(product)}
                          className="p-1.5 text-gray-400 hover:text-lodiss-red"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(product.id)}
                          className="p-1.5 text-gray-400 hover:text-red-500"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-gray-500">
              Affichage de {(page - 1) * 20 + 1} à {Math.min(page * 20, total)} sur {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 border border-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-50"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="text-sm">{page} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 border border-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-50"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-bold">
                  {editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
                </h2>
                <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded">
                  <X size={20} />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                    <select
                      value={form.category_id}
                      onChange={e => setForm({ ...form, category_id: Number(e.target.value), subcategory_id: 0 })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    >
                      <option value={0}>Sélectionner</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name_fr}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sous-catégorie</label>
                    <select
                      value={form.subcategory_id}
                      onChange={e => setForm({ ...form, subcategory_id: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    >
                      <option value={0}>Sélectionner</option>
                      {form.category_id > 0 && categories.find(c => c.id === form.category_id)?.subcategories.map(sub => (
                        <option key={sub.id} value={sub.id}>{sub.name_fr}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Référence *</label>
                    <input
                      type="text"
                      value={form.ref}
                      onChange={e => setForm({ ...form, ref: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Marque</label>
                    <input
                      type="text"
                      value={form.brand}
                      onChange={e => setForm({ ...form, brand: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom (FR) *</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom (EN)</label>
                    <input
                      type="text"
                      value={form.name_en}
                      onChange={e => setForm({ ...form, name_en: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prix public (TND) *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={form.price}
                      onChange={e => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                      required
                    />
                  </div>
                  <div className="border-l border-gray-200 pl-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prix revendeur (TND)</label>
                    <input
                      type="number"
                      step="0.001"
                      value={form.reseller_price ?? ''}
                      onChange={e => setForm({ ...form, reseller_price: e.target.value === '' ? null : parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                      placeholder="Laissez vide pour -30% auto"
                    />
                    <p className="text-xs text-gray-400 mt-1">Si vide, le prix revendeur sera calculé automatiquement (-30% du prix public).</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stock</label>
                    <input
                      type="number"
                      value={form.stock}
                      onChange={e => setForm({ ...form, stock: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">MOQ</label>
                    <input
                      type="number"
                      value={form.moq}
                      onChange={e => setForm({ ...form, moq: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Seuil devis</label>
                    <input
                      type="number"
                      value={form.quote_threshold}
                      onChange={e => setForm({ ...form, quote_threshold: parseInt(e.target.value) || 10 })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description (FR)</label>
                    <textarea
                      value={form.description}
                      onChange={e => setForm({ ...form, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description (EN)</label>
                    <textarea
                      value={form.description_en}
                      onChange={e => setForm({ ...form, description_en: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Images</label>
                  {editingProduct ? (
                    <ImageUpload
                      images={form.images}
                      productId={editingProduct.id}
                      onChange={(images) => setForm({ ...form, images })}
                    />
                  ) : (
                    <div>
                      <ImageUpload
                        images={form.images}
                        productId={undefined}
                        onChange={(images) => setForm({ ...form, images })}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Les images seront ajoutées après la création du produit
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.bestseller}
                      onChange={e => setForm({ ...form, bestseller: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">Bestseller</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={e => setForm({ ...form, is_active: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">Actif</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-3 p-4 border-t">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !form.name || !form.ref}
                  className="px-4 py-2 bg-lodiss-red text-white rounded-lg hover:bg-lodiss-dark disabled:opacity-50"
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md">
              <h3 className="text-lg font-bold mb-2">Confirmer la suppression</h3>
              <p className="text-gray-500 mb-4">
                Êtes-vous sûr de vouloir supprimer ce produit ? Cette action est irréversible.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
